import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@blueprintjs/core';
import EnhancedDialog from '../EnhancedDialog';
import { useCanExportGridData } from '../../utils/gridExportCapability';
import { collectExportRows, createExportBlob, exportColumnFields, exportFilename, type ExportColumn, type ExportPageLoader, type ExportRow, type ExportRowOrder } from '../../utils/dataExport';
import './DataExport.css';
import '../ui/ResetColumnsButton.css';

export interface ExportConfiguration {
  name: string;
  columns: ExportColumn[];
  load: ExportPageLoader;
  rowKey?: string;
  selectedRows?: ExportRow[];
  /** Open on "Selected records" when rows are selected (REG-917: picking rows means exporting them). */
  preferSelected?: boolean;
  filterSummary: string;
  /**
   * The grid's row order. Rows load in id order (so a long export never skips
   * or repeats one); the file is then put in the order the grid shows.
   */
  order?: ExportRowOrder;
}

export function ExportButton({ getConfiguration, disabled = false, allowed }: {
  getConfiguration: () => ExportConfiguration;
  disabled?: boolean;
  /**
   * Who may export, when it is not the grid export capability: Opportunities
   * tabs let every user download the leads they already see.
   */
  allowed?: boolean;
}) {
  const hasExportCapability = useCanExportGridData();
  const canExport = allowed ?? hasExportCapability;
  const [config, setConfig] = useState<ExportConfiguration | null>(null);
  if (!canExport) return null;
  return <>
    {/* Same pill as Reset columns and Save as default layout, which sit next to it on every grid. */}
    <button
      type="button"
      className="sky-grid-reset-button"
      title="Export these rows to Excel or CSV"
      disabled={disabled}
      onClick={() => setConfig(getConfiguration())}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 3v12" />
        <path d="M7 10l5 5 5-5" />
        <path d="M5 21h14" />
      </svg>
      Export
    </button>
    {config && <ExportDialog config={config} allowed={allowed} onClose={() => setConfig(null)} />}
  </>;
}

export function ExportDialog({ config, onClose, allowed }: { config: ExportConfiguration; onClose: () => void; allowed?: boolean }) {
  const [format, setFormat] = useState<'csv' | 'xlsx'>('xlsx');
  const [name, setName] = useState(`${config.name}-${new Date().toISOString().slice(0, 10)}`);
  const [selected, setSelected] = useState(() => new Set(config.columns.filter((c) => c.visible).map((c) => c.key)));
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'selected'>(() => (config.preferSelected && config.selectedRows?.length ? 'selected' : 'all'));
  const [status, setStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [progress, setProgress] = useState({ loaded: 0, total: null as number | null });
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  const hasExportCapability = useCanExportGridData();
  const canExport = allowed ?? hasExportCapability;
  useEffect(() => () => controller.current?.abort(), []);
  const close = () => { controller.current?.abort(); onClose(); };
  const download = async () => {
    if (!canExport || !selected.size) return;
    const abort = new AbortController();
    controller.current = abort;
    setStatus('loading'); setError(''); setNotice(''); setProgress({ loaded: 0, total: null });
    try {
      const selectedIds = scope === 'selected' && config.rowKey ? (config.selectedRows ?? [])
        .map((row) => row[config.rowKey!] as number | string)
        .sort((a, b) => typeof a === 'number' && typeof b === 'number' ? a - b : String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0) : undefined;
      const chosen = config.columns.filter((c) => selected.has(c.key));
      const fields = [...new Set([...exportColumnFields(chosen), ...(config.order?.fields ?? [])])];
      const loaded = scope === 'selected' && !config.rowKey
        ? config.selectedRows ?? []
        : await collectExportRows(config.load, abort.signal, (count, total, note) => { setProgress({ loaded: count, total }); setNotice(note ?? ''); }, config.rowKey, fields, selectedIds);
      const rows = config.order ? config.order.sort(loaded) : loaded;
      abort.signal.throwIfAborted();
      if (selectedIds && rows.length !== selectedIds.length) throw new Error('Some selected records changed or are no longer available. Close this dialog and select the records again.');
      if (!rows.length) throw new Error('No records match these filters. Adjust the filters and try again.');
      const blob = await createExportBlob(rows, chosen, format);
      abort.signal.throwIfAborted();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = exportFilename(name, format); link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setProgress({ loaded: rows.length, total: rows.length }); setStatus('done');
    } catch (cause) {
      if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : 'The export failed. Please try again.');
      setStatus('idle');
    }
  };
  const busy = status === 'loading';
  return createPortal(<EnhancedDialog isOpen title={`Export ${config.name}`} onClose={close} style={{ width: 660, maxWidth: '95vw' }}>
    <div className="sky-export-dialog">
      <p className="sky-export-lead">Download every matching record, across all pages.</p>
      <div className="sky-export-summary"><strong>Filters applied</strong><span>{config.filterSummary}</span></div>
      <fieldset disabled={busy}>
        <div className="sky-export-options">
          <label>File format<select aria-label="File format" value={format} onChange={(e) => setFormat(e.target.value as 'csv' | 'xlsx')}><option value="xlsx">Excel (.xlsx)</option><option value="csv">CSV (.csv)</option></select></label>
          <label>Records<select aria-label="Records to export" value={scope} onChange={(e) => setScope(e.target.value as 'all' | 'selected')}><option value="all">All matching records</option><option value="selected" disabled={!config.selectedRows?.length}>Selected records ({config.selectedRows?.length ?? 0})</option></select></label>
        </div>
        <label className="sky-export-filename">File name<input aria-label="Export file name" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <div className="sky-export-column-heading"><strong>Columns <span>{selected.size} selected</span></strong><div>
          <button type="button" onClick={() => setSelected(new Set(config.columns.filter((c) => c.visible).map((c) => c.key)))}>Visible</button>
          <button type="button" onClick={() => setSelected(new Set(config.columns.map((c) => c.key)))}>All</button>
          <button type="button" onClick={() => setSelected(new Set())}>Clear</button>
        </div></div>
        <input className="sky-export-column-search" aria-label="Search export columns" placeholder="Find a column..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="sky-export-columns">{config.columns.filter((c) => c.label.toLowerCase().includes(search.toLowerCase())).map((column) => <label key={column.key}>
          <input type="checkbox" checked={selected.has(column.key)} onChange={() => setSelected((current) => { const next = new Set(current); if (next.has(column.key)) next.delete(column.key); else next.add(column.key); return next; })} />{column.label}
        </label>)}</div>
      </fieldset>
      {busy && <div className="sky-export-progress" role="status"><progress max={progress.total ?? undefined} value={progress.total ? progress.loaded : undefined} /><span>Preparing {progress.loaded.toLocaleString()}{progress.total !== null ? ` of ${progress.total.toLocaleString()}` : ''} records...</span></div>}
      {status === 'done' && <div className="sky-export-success" role="status">Downloaded {progress.loaded.toLocaleString()} {progress.loaded === 1 ? 'record' : 'records'}.{notice ? ` ${notice}` : ''}</div>}
      {error && <div className="sky-export-error" role="alert">{error}</div>}
      <div className="sky-export-footer"><span>Only data you have permission to view is included.</span><Button onClick={close}>{busy ? 'Cancel export' : 'Close'}</Button><Button intent="primary" icon="download" disabled={busy || !selected.size || !canExport} onClick={() => { void download(); }}>{status === 'done' ? 'Download again' : 'Download'}</Button></div>
    </div>
  </EnhancedDialog>, document.body);
}
